/**
 * A forgiving HTML tokenizer: text, tags, comments, declarations and the raw contents
 * of `script`, `style`, `textarea` and their kin, each with its exact source text and
 * offsets.
 *
 * It follows the HTML tokenizer where that matters for these tools — tag and attribute
 * syntax, unquoted values, `<!-->`, raw-text elements whose content is never markup —
 * and stops short of the full specification's error recovery. A `<` that does not start
 * markup is text; a tag still open at the end of the input is reported as `incomplete`
 * rather than silently dropped.
 */

export interface HtmlAttribute {
  /** Lower-cased. */
  name: string;
  /** The value as written (quotes removed, references not decoded), or null when bare. */
  value: string | null;
  /** The attribute exactly as it appears in the source. */
  raw: string;
}

interface TokenBase {
  raw: string;
  start: number;
  end: number;
}

export type HtmlToken =
  | (TokenBase & { kind: "text" })
  | (TokenBase & {
      kind: "start";
      /** Lower-cased. */
      name: string;
      attrs: HtmlAttribute[];
      selfClosing: boolean;
    })
  | (TokenBase & { kind: "end"; name: string })
  | (TokenBase & { kind: "comment"; terminated: boolean })
  | (TokenBase & { kind: "declaration" })
  | (TokenBase & { kind: "rawtext"; parent: string })
  | (TokenBase & { kind: "incomplete" });

/** Elements whose content is text up to their own end tag, never markup. */
export const RAW_TEXT_ELEMENTS = new Set([
  "script",
  "style",
  "textarea",
  "title",
  "xmp",
  "iframe",
  "noembed",
  "noframes",
  "noscript",
  "plaintext",
]);

const RE_LETTER = /^[A-Za-z]$/;
const RE_SPACE = /^[ \t\n\f\r]$/;
const RE_TAG_NAME = /[^\s/>]+/y;
const RE_ATTRIBUTE_NAME = /[^\s/>][^\s/>=]*/y;
const RE_UNQUOTED_VALUE = /[^\s>]*/y;

function isSpace(ch: string | undefined): boolean {
  return ch !== undefined && RE_SPACE.test(ch);
}

function readSticky(pattern: RegExp, input: string, pos: number): string {
  pattern.lastIndex = pos;
  return pattern.exec(input)?.[0] ?? "";
}

function skipSpace(input: string, pos: number): number {
  let index = pos;
  while (isSpace(input[index])) {
    index += 1;
  }
  return index;
}

/** Up to and including `terminator`, or to the end of the input. */
function through(
  input: string,
  from: number,
  terminator: string
): { end: number; terminated: boolean } {
  const found = input.indexOf(terminator, from);
  if (found === -1) {
    return { end: input.length, terminated: false };
  }
  return { end: found + terminator.length, terminated: true };
}

function readComment(input: string, start: number): HtmlToken {
  // `<!-->` and `<!--->` are complete (if empty) comments in HTML.
  for (const abrupt of ["<!-->", "<!--->"]) {
    if (input.startsWith(abrupt, start)) {
      const end = start + abrupt.length;
      return {
        kind: "comment",
        raw: input.slice(start, end),
        start,
        end,
        terminated: true,
      };
    }
  }
  const { end, terminated } = through(input, start + 4, "-->");
  return {
    kind: "comment",
    raw: input.slice(start, end),
    start,
    end,
    terminated,
  };
}

function readDeclaration(input: string, start: number): HtmlToken {
  const terminator = input.startsWith("<![CDATA[", start) ? "]]>" : ">";
  const { end, terminated } = through(input, start + 2, terminator);
  const raw = input.slice(start, end);
  if (!terminated) {
    return { kind: "incomplete", raw, start, end };
  }
  return { kind: "declaration", raw, start, end };
}

function readEndTag(input: string, start: number): HtmlToken {
  if (!RE_LETTER.test(input[start + 2] ?? "")) {
    // `</>` is ignored by browsers; `</3` and friends are bogus comments.
    const { end } = through(input, start + 2, ">");
    return { kind: "declaration", raw: input.slice(start, end), start, end };
  }
  const name = readSticky(RE_TAG_NAME, input, start + 2).toLowerCase();
  const { end, terminated } = through(input, start + 2, ">");
  const raw = input.slice(start, end);
  if (!terminated) {
    return { kind: "incomplete", raw, start, end };
  }
  return { kind: "end", name, raw, start, end };
}

/** One attribute at `pos`; null when the tag runs off the end of the input. */
function readAttribute(
  input: string,
  pos: number
): { attribute: HtmlAttribute; end: number } | null {
  const name = readSticky(RE_ATTRIBUTE_NAME, input, pos);
  let index = skipSpace(input, pos + name.length);
  if (input[index] !== "=") {
    const end = pos + name.length;
    return {
      attribute: { name: name.toLowerCase(), value: null, raw: name },
      end,
    };
  }
  index = skipSpace(input, index + 1);
  const quote = input[index];
  let value: string;
  if (quote === '"' || quote === "'") {
    const close = input.indexOf(quote, index + 1);
    if (close === -1) {
      return null;
    }
    value = input.slice(index + 1, close);
    index = close + 1;
  } else {
    value = readSticky(RE_UNQUOTED_VALUE, input, index);
    index += value.length;
  }
  return {
    attribute: {
      name: name.toLowerCase(),
      value,
      raw: input.slice(pos, index),
    },
    end: index,
  };
}

function readStartTag(input: string, start: number): HtmlToken {
  const name = readSticky(RE_TAG_NAME, input, start + 1);
  const attrs: HtmlAttribute[] = [];
  let index = start + 1 + name.length;
  while (index < input.length) {
    index = skipSpace(input, index);
    if (input[index] === ">") {
      const end = index + 1;
      const raw = input.slice(start, end);
      return {
        kind: "start",
        name: name.toLowerCase(),
        attrs,
        selfClosing: false,
        raw,
        start,
        end,
      };
    }
    if (input.startsWith("/>", index)) {
      const end = index + 2;
      const raw = input.slice(start, end);
      return {
        kind: "start",
        name: name.toLowerCase(),
        attrs,
        selfClosing: true,
        raw,
        start,
        end,
      };
    }
    if (input[index] === "/") {
      index += 1;
      continue;
    }
    if (index >= input.length) {
      break;
    }
    const read = readAttribute(input, index);
    if (read === null) {
      break;
    }
    attrs.push(read.attribute);
    index = read.end;
  }
  return {
    kind: "incomplete",
    raw: input.slice(start),
    start,
    end: input.length,
  };
}

/** The markup starting at `start` (a `<`), or null when that `<` is just text. */
function readMarkup(input: string, start: number): HtmlToken | null {
  if (input.startsWith("<!--", start)) {
    return readComment(input, start);
  }
  if (input.startsWith("<!", start) || input.startsWith("<?", start)) {
    return readDeclaration(input, start);
  }
  if (input.startsWith("</", start)) {
    return readEndTag(input, start);
  }
  if (RE_LETTER.test(input[start + 1] ?? "")) {
    return readStartTag(input, start);
  }
  return null;
}

/** Offset of `</name` (followed by space, `/` or `>`) at or after `from`, or the end. */
function rawTextEnd(input: string, name: string, from: number): number {
  const lower = input.toLowerCase();
  let index = lower.indexOf(`</${name}`, from);
  while (index !== -1) {
    const after = input[index + name.length + 2];
    if (
      after === undefined ||
      after === ">" ||
      after === "/" ||
      isSpace(after)
    ) {
      return index;
    }
    index = lower.indexOf(`</${name}`, index + 1);
  }
  return input.length;
}

export function tokenizeHtml(input: string): HtmlToken[] {
  const tokens: HtmlToken[] = [];
  let pos = 0;
  let textStart = 0;
  const flushText = (end: number) => {
    if (end > textStart) {
      tokens.push({
        kind: "text",
        raw: input.slice(textStart, end),
        start: textStart,
        end,
      });
    }
  };
  while (pos < input.length) {
    const lt = input.indexOf("<", pos);
    if (lt === -1) {
      break;
    }
    const token = readMarkup(input, lt);
    if (token === null) {
      pos = lt + 1;
      continue;
    }
    flushText(lt);
    tokens.push(token);
    pos = token.end;
    textStart = pos;
    if (
      token.kind === "start" &&
      !token.selfClosing &&
      RAW_TEXT_ELEMENTS.has(token.name)
    ) {
      const close = rawTextEnd(input, token.name, pos);
      if (close > pos) {
        tokens.push({
          kind: "rawtext",
          raw: input.slice(pos, close),
          start: pos,
          end: close,
          parent: token.name,
        });
      }
      pos = close;
      textStart = close;
    }
  }
  flushText(input.length);
  return tokens;
}
